import { QuizEngine, Phase } from './quiz/engine';
import type { GeneratedQuestion, Difficulty } from './generators/math/types';

export type LessonState = 'warmup' | 'guide' | 'challenge' | 'done';

export interface LessonPhases {
  warmup: GeneratedQuestion[];
  guide: GeneratedQuestion[];
  challenge: GeneratedQuestion[];
}

interface SubmitResult {
  correct: boolean;
  xp: number;
  /** Combo count after this answer (0 if wrong / 1+ if correct streak). Added for C07 mascot streak detection. */
  combo: number;
}

const PHASE_ORDER: Exclude<LessonState, 'done'>[] = ['warmup', 'guide', 'challenge'];

export class LessonController {
  state: LessonState = 'warmup';
  totalXP = 0;
  private idx = 0;
  private engine = new QuizEngine();
  private phases: LessonPhases;
  private difficulty: Difficulty;
  public recordKP?: (correct: boolean) => void;

  constructor(phases: LessonPhases, difficulty: Difficulty, recordKP?: (correct: boolean) => void) {
    this.phases = phases;
    this.difficulty = difficulty;
    this.recordKP = recordKP;
    this.advanceUntilNonEmpty();
  }

  currentQuestion(): GeneratedQuestion {
    if (this.state === 'done') throw new Error('No current question — lesson done');
    return this.phases[this.state][this.idx];
  }

  positionInPhase(): { current: number; total: number } {
    if (this.state === 'done') return { current: 0, total: 0 };
    return { current: this.idx + 1, total: this.phases[this.state].length };
  }

  submitAnswer(answer: number | string): SubmitResult {
    if (this.state === 'done') throw new Error('Lesson already done');
    const q = this.currentQuestion();
    let correct: boolean;
    if (q.kind === 'mcq') {
      correct = typeof answer === 'number' && answer === q.correctAnswer;
    } else if (q.kind === 'fill') {
      // fill: compare string (trimmed)
      const accepted = q.acceptedAnswers ?? [q.correctAnswer];
      const trimmed = typeof answer === 'string' ? answer.trim() : '';
      correct = accepted.includes(trimmed);
    } else {
      // worked-example: not gradable (never reaches game lesson path).
      correct = false;
    }
    const phase = this.state as Phase;
    const result = this.engine.recordAnswer({ correct, difficulty: this.difficulty, phase });
    this.totalXP += result.xpAwarded;
    this.recordKP?.(correct);
    if (correct) {
      this.idx++;
      this.advanceUntilNonEmpty();
    }
    // wrong: stay on same idx (retry)
    return { correct, xp: result.xpAwarded, combo: result.combo };
  }

  private advanceUntilNonEmpty(): void {
    while (this.state !== 'done' && this.idx >= this.phases[this.state].length) {
      const currentPhaseIdx = PHASE_ORDER.indexOf(this.state as Exclude<LessonState, 'done'>);
      if (currentPhaseIdx === PHASE_ORDER.length - 1) {
        this.state = 'done';
      } else {
        this.state = PHASE_ORDER[currentPhaseIdx + 1];
        this.idx = 0;
      }
    }
  }
}
